// Markdown → Block[] parser for "Magic Paste".
//
// Goal: when an editor pastes a chunk of Markdown (or plain text with
// Markdown-ish markers) into a paragraph, transform it into a list of
// editorial blocks (headings / paragraphs / lists / quotes / images /
// dividers / embeds / link-cards).
//
// This is intentionally a minimal, hand-rolled parser — no external
// dependency. It covers the patterns we care about for editorial paste
// flows (Google Docs → MD export, ChatGPT replies, casual markdown).
//
// Inline markdown (**bold**, *italic*, `code`, [text](url)) is supported
// only inside paragraph blocks via inlineToHtml().

import {
  type Block,
  type DividerBlock,
  type HeadingBlock,
  type ImageBlock,
  type LinkCardBlock,
  type ListBlock,
  type ParagraphBlock,
  type QuoteBlock,
  type VideoEmbedBlock,
  type AudioEmbedBlock,
  type EmbedProvider,
  detectProvider,
  makeBlock,
} from "./blocks";

const VIDEO_PROVIDERS: EmbedProvider[] = [
  "youtube",
  "vimeo",
  "instagram",
  "tiktok",
];

const AUDIO_PROVIDERS: EmbedProvider[] = [
  "soundcloud",
  "spotify",
  "bandcamp",
  "apple_music",
  "artlist",
];

export function markdownToBlocks(text: string): Block[] {
  if (!text) return [];
  // Normalize newlines, trim trailing whitespace per line.
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.replace(/[\t ]+$/g, ""));

  const out: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — paragraph separator. Skip.
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Horizontal rule: ---, ***, ___
    if (/^(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      out.push(divider());
      i++;
      continue;
    }

    // Heading 1/2/3
    const h = /^(#{1,3})\s+(.+)$/.exec(line);
    if (h) {
      const level = h[1].length as 1 | 2 | 3;
      out.push(heading(level, h[2].trim()));
      i++;
      continue;
    }

    // Blockquote — consume consecutive ">" lines (allow blank ">" as soft break).
    if (/^>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      out.push(quote(buf.join("\n").trim()));
      continue;
    }

    // Unordered list — -, *, +
    if (/^[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+]\s+/, "").trim());
        i++;
      }
      out.push(listBlock(false, items));
      continue;
    }

    // Ordered list — 1. / 2. / 3.
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, "").trim());
        i++;
      }
      out.push(listBlock(true, items));
      continue;
    }

    // Image (standalone): ![alt](url "caption")
    const img = /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)\s*$/.exec(line);
    if (img) {
      out.push(imageFromUrl(img[2], img[1], img[3]));
      i++;
      continue;
    }

    // Standalone link: [text](url)
    const lnk = /^\[([^\]]+)\]\(([^)\s]+)\)\s*$/.exec(line);
    if (lnk) {
      out.push(linkOrEmbed(lnk[2], lnk[1]));
      i++;
      continue;
    }

    // Pure URL line.
    const urlMatch = /^(https?:\/\/\S+)\s*$/.exec(line);
    if (urlMatch) {
      out.push(linkOrEmbed(urlMatch[1]));
      i++;
      continue;
    }

    // Otherwise — paragraph. Consume consecutive non-blank, non-structural lines.
    const buf: string[] = [line];
    i++;
    while (i < lines.length) {
      const nxt = lines[i];
      if (nxt.trim() === "") break;
      if (
        /^(#{1,3})\s+/.test(nxt) ||
        /^>\s?/.test(nxt) ||
        /^[-*+]\s+/.test(nxt) ||
        /^\d+\.\s+/.test(nxt) ||
        /^(?:-{3,}|\*{3,}|_{3,})\s*$/.test(nxt) ||
        /^!\[([^\]]*)\]\(([^)\s]+)/.test(nxt) ||
        /^https?:\/\/\S+\s*$/.test(nxt)
      ) {
        break;
      }
      buf.push(nxt);
      i++;
    }
    out.push(paragraph(inlineToHtml(buf.join(" ").trim())));
  }

  return out;
}

// ── Inline markdown → sanitized inline HTML ───────────────────────────────

export function inlineToHtml(input: string): string {
  if (!input) return "";
  // Escape HTML first (we'll only emit our own tags afterwards).
  let s = input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Inline code (single backtick) — temporarily protect contents.
  const codeStash: string[] = [];
  s = s.replace(/`([^`]+)`/g, (_m, code) => {
    codeStash.push(code);
    return ` CODE${codeStash.length - 1} `;
  });

  // Links: [text](url) — only http(s).
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    (_m, text, url) => `<a href="${url}">${text}</a>`,
  );

  // Bold: **text** or __text__
  s = s.replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>");
  s = s.replace(/__([^_]+)__/g, "<b>$1</b>");

  // Italic: *text* or _text_
  s = s.replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, "$1<i>$2</i>");
  s = s.replace(/(^|[^_])_([^_\s][^_]*?)_(?!_)/g, "$1<i>$2</i>");

  // Restore code.
  s = s.replace(/ CODE(\d+) /g, (_m, n) => {
    const code = codeStash[Number(n)] || "";
    return `<code>${code}</code>`;
  });

  return s;
}

// ── Factories ─────────────────────────────────────────────────────────────

function heading(level: 1 | 2 | 3, text: string): HeadingBlock {
  const b = makeBlock("heading") as HeadingBlock;
  b.level = level;
  b.text = text;
  return b;
}

function paragraph(html: string): ParagraphBlock {
  const b = makeBlock("paragraph") as ParagraphBlock;
  b.html = html;
  return b;
}

function quote(text: string): QuoteBlock {
  const b = makeBlock("quote") as QuoteBlock;
  b.text = text;
  return b;
}

function divider(): DividerBlock {
  const b = makeBlock("divider") as DividerBlock;
  b.style = "line";
  return b;
}

function listBlock(ordered: boolean, items: string[]): ListBlock {
  const b = makeBlock("list") as ListBlock;
  b.ordered = ordered;
  b.items = items.length > 0 ? items : [""];
  return b;
}

function imageFromUrl(url: string, alt?: string, caption?: string): ImageBlock {
  const b = makeBlock("image") as ImageBlock;
  b.url = url;
  if (alt) b.alt = alt;
  if (caption) b.caption = caption;
  return b;
}

function linkOrEmbed(url: string, title?: string): Block {
  const provider = detectProvider(url);
  if (VIDEO_PROVIDERS.includes(provider)) {
    const b = makeBlock("video_embed") as VideoEmbedBlock;
    b.url = url;
    b.provider = provider;
    return b;
  }
  if (AUDIO_PROVIDERS.includes(provider)) {
    const b = makeBlock("audio_embed") as AudioEmbedBlock;
    b.url = url;
    b.provider = provider;
    return b;
  }
  const b = makeBlock("link_card") as LinkCardBlock;
  b.url = url;
  if (title) b.title = title;
  return b;
}
